import { Box, Typography, useTheme, Button } from "@mui/material";
import { useState }      from "react";

import AddIcon           from "@mui/icons-material/Add";
import EditOutlinedIcon  from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import CardGiftcardIcon  from "@mui/icons-material/CardGiftcard";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import FixedSizeDialog   from "components/fixedSizeDialog";
import EmployeeRenumerationForm from "../forms/employeeForms/employeeRenumerationCreateForm";
import EmployeeDeductionForm    from "../forms/employeeForms/employeeDeductionCreateForm";
import EmployeeAllowanceForm    from "../forms/employeeForms/employeeAllowanceCreateForm";
import { useEmployeeRenumerationsList, useEmployeeDeductionsList, useEmployeeAllowancesList } from "api/hooks/humanResource";
import { requester }     from "api/pesabackend";
import { currencyFormatter } from "utilities/currencyFormatter";

/* ── Reusable payroll section ── */
const PayrollSection = ({ title, icon, accentColor, borderColor, hoverBg, rows, loading, columns, renderRow, onAdd, addLabel, children }) => {
  const theme  = useTheme();
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.75rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box sx={{ width: "34px", height: "34px", borderRadius: "9px", backgroundColor: `${accentColor}15`, display: "flex", alignItems: "center", justifyContent: "center", color: accentColor, flexShrink: 0 }}>
            {icon}
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "16px", fontWeight: 400, color: textPri }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: textSec }}>{rows.length} entries</Typography>
          </Box>
        </Box>
        <Button onClick={onAdd} startIcon={<AddIcon sx={{ fontSize: "15px" }} />}
          sx={{ fontSize: "12px", fontWeight: 500, px: "0.875rem", py: "0.35rem", borderRadius: "8px", border: `1px solid ${accentColor}50`, color: accentColor, backgroundColor: "transparent", textTransform: "none", "&:hover": { backgroundColor: `${accentColor}10` } }}>
          {addLabel}
        </Button>
      </Box>
      <ReportTable columns={columns} rows={rows} loading={loading} emptyMessage={`No ${title.toLowerCase()} added yet`}
        renderRow={renderRow}
      />
      {children}
    </Box>
  );
};

export default function EmployeePayrollTable({ setIsSuccess, employeeId }) {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;

  const { employeeRenumerationList, setNewEmployeeRenumerationData } = useEmployeeRenumerationsList(1, employeeId);
  const { employeeAllowanceList,    setNewEmployeeAllowanceData }    = useEmployeeAllowancesList(1, employeeId);
  const { employeeDeductionList,    setNewEmployeeDeductionData }    = useEmployeeDeductionsList(1, employeeId);

  /* Renumeration state */
  const [openR,      setOpenR]      = useState(false);
  const [openEditR,  setOpenEditR]  = useState(false);
  const [openDelR,   setOpenDelR]   = useState(false);
  const [editR,      setEditR]      = useState({});
  const [clickedR,   setClickedR]   = useState("");

  /* Allowance state */
  const [openA,      setOpenA]      = useState(false);
  const [openEditA,  setOpenEditA]  = useState(false);
  const [openDelA,   setOpenDelA]   = useState(false);
  const [editA,      setEditA]      = useState({});
  const [clickedA,   setClickedA]   = useState("");

  /* Deduction state */
  const [openD,      setOpenD]      = useState(false);
  const [openEditD,  setOpenEditD]  = useState(false);
  const [openDelD,   setOpenDelD]   = useState(false);
  const [editD,      setEditD]      = useState({});
  const [clickedD,   setClickedD]   = useState("");

  const fetchPayroll = async (id) => {
    const res = await requester.get(`/human_resource/employees/payroll/get/${id}`);
    return res.status === 200 ? res.data.data : null;
  };

  const deleteWarning = (isDark) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", backgroundColor: "rgba(226,75,74,0.07)", border: "1px solid rgba(226,75,74,0.2)", borderRadius: "8px", p: "0.875rem 1rem" }}>
      <DeleteOutlineIcon sx={{ fontSize: "18px", color: "#e24b4a", flexShrink: 0 }} />
      <Typography sx={{ fontSize: "13px", color: isDark ? "#f09595" : "#a32d2d", lineHeight: 1.6 }}>This will permanently delete the entry and cannot be undone.</Typography>
    </Box>
  );

  const actionBtns = (onEdit, onDelete) => (
    <Box sx={{ display: "flex", gap: "5px", justifyContent: "center" }}>
      <Box onClick={onEdit} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(186,117,23,0.3)", cursor: "pointer", color: "#BA7517", fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(186,117,23,0.08)" } }}>
        <EditOutlinedIcon sx={{ fontSize: "14px" }} /> Edit
      </Box>
      <Box onClick={onDelete} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(226,75,74,0.3)", cursor: "pointer", color: "#e24b4a", fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(226,75,74,0.08)" } }}>
        <DeleteOutlineIcon sx={{ fontSize: "14px" }} /> Delete
      </Box>
    </Box>
  );

  const payrollCols = [
    { label: "#", width: 40 }, { label: "Type" }, { label: "Amount", align: "right" }, { label: "Frequency" }, { label: "Actions", align: "center" },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "2rem" }}>

      {/* ── Renumeration ── */}
      <PayrollSection title="Renumeration" icon={<MonetizationOnIcon sx={{ fontSize: "18px" }} />} accentColor={accent}
        rows={employeeRenumerationList} loading={false} columns={payrollCols}
        onAdd={() => setOpenR(true)} addLabel="Add Renumeration"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell>{index + 1}</RCell>
            <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.related_renumeration_type.renumeration_name}</Typography></RCell>
            <RCell align="right">{currencyFormatter(parseFloat(row.amount))}</RCell>
            <RCell>{row.frequency}</RCell>
            <RCell align="center">{actionBtns(
              async () => { const d = await fetchPayroll(row.id); if (d) { setEditR(d); setOpenEditR(true); } },
              async () => { const d = await fetchPayroll(row.id); if (d) { setClickedR(d.id); setOpenDelR(true); } }
            )}</RCell>
          </RRow>
        )}
      >
        <FixedSizeDialog open={openR} handleClose={() => setOpenR(false)} dialogHeading="Add Renumeration" negativeResponse="Cancel" maxWidth="md"
          content={<Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}><EmployeeRenumerationForm formType="create" setOpen={setOpenR} setNewEmployeeRenumerationData={setNewEmployeeRenumerationData} setIsSuccess={setIsSuccess} editData={editR} employeeId={employeeId} /></Box>} />
        <FixedSizeDialog open={openEditR} handleClose={() => setOpenEditR(false)} dialogHeading="Edit Renumeration" negativeResponse="Cancel" maxWidth="md"
          content={<Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}><EmployeeRenumerationForm formType="edit" setOpen={setOpenEditR} setNewEmployeeRenumerationData={setNewEmployeeRenumerationData} setIsSuccess={setIsSuccess} editData={editR} employeeId={employeeId} /></Box>} />
        <FixedSizeDialog open={openDelR} handleClose={() => setOpenDelR(false)} dialogHeading="Delete renumeration?" negativeResponse="Cancel"
          positiveResponse="Yes, delete" handleSubmit={async () => { await requester.post(`/human_resource/employees/payroll/delete/${clickedR}`); setIsSuccess(true); setNewEmployeeRenumerationData(true); setOpenDelR(false); }} maxWidth="sm" content={deleteWarning(isDark)} />
      </PayrollSection>

      {/* ── Allowances ── */}
      <PayrollSection title="Allowances" icon={<CardGiftcardIcon sx={{ fontSize: "18px" }} />} accentColor="#BA7517"
        rows={employeeAllowanceList} loading={false} columns={payrollCols}
        onAdd={() => setOpenA(true)} addLabel="Add Allowance"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell>{index + 1}</RCell>
            <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.related_allowance_type.allowance_name}</Typography></RCell>
            <RCell align="right">{currencyFormatter(parseFloat(row.amount))}</RCell>
            <RCell>{row.frequency}</RCell>
            <RCell align="center">{actionBtns(
              async () => { const d = await fetchPayroll(row.id); if (d) { setEditA(d); setOpenEditA(true); } },
              async () => { const d = await fetchPayroll(row.id); if (d) { setClickedA(d.id); setOpenDelA(true); } }
            )}</RCell>
          </RRow>
        )}
      >
        <FixedSizeDialog open={openA} handleClose={() => setOpenA(false)} dialogHeading="Add Allowance" negativeResponse="Cancel" maxWidth="md"
          content={<Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}><EmployeeAllowanceForm formType="create" setOpen={setOpenA} setNewEmployeeAllowanceData={setNewEmployeeAllowanceData} setIsSuccess={setIsSuccess} editData={editA} employeeId={employeeId} /></Box>} />
        <FixedSizeDialog open={openEditA} handleClose={() => setOpenEditA(false)} dialogHeading="Edit Allowance" negativeResponse="Cancel" maxWidth="md"
          content={<Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}><EmployeeAllowanceForm formType="edit" setOpen={setOpenEditA} setNewEmployeeAllowanceData={setNewEmployeeAllowanceData} setIsSuccess={setIsSuccess} editData={editA} employeeId={employeeId} /></Box>} />
        <FixedSizeDialog open={openDelA} handleClose={() => setOpenDelA(false)} dialogHeading="Delete allowance?" negativeResponse="Cancel"
          positiveResponse="Yes, delete" handleSubmit={async () => { await requester.post(`/human_resource/employees/payroll/delete/${clickedA}`); setIsSuccess(true); setNewEmployeeAllowanceData(true); setOpenDelA(false); }} maxWidth="sm" content={deleteWarning(isDark)} />
      </PayrollSection>

      {/* ── Deductions ── */}
      <PayrollSection title="Deductions" icon={<RemoveCircleOutlineIcon sx={{ fontSize: "18px" }} />} accentColor="#e24b4a"
        rows={employeeDeductionList} loading={false} columns={payrollCols}
        onAdd={() => setOpenD(true)} addLabel="Add Deduction"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell>{index + 1}</RCell>
            <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.related_deduction_type.deduction_name}</Typography></RCell>
            <RCell align="right">{currencyFormatter(parseFloat(row.amount))}</RCell>
            <RCell>{row.frequency}</RCell>
            <RCell align="center">{actionBtns(
              async () => { const d = await fetchPayroll(row.id); if (d) { setEditD(d); setOpenEditD(true); } },
              async () => { const d = await fetchPayroll(row.id); if (d) { setClickedD(d.id); setOpenDelD(true); } }
            )}</RCell>
          </RRow>
        )}
      >
        <FixedSizeDialog open={openD} handleClose={() => setOpenD(false)} dialogHeading="Add Deduction" negativeResponse="Cancel" maxWidth="md"
          content={<Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}><EmployeeDeductionForm formType="create" setOpen={setOpenD} setNewEmployeeDeductionData={setNewEmployeeDeductionData} setIsSuccess={setIsSuccess} editData={editD} employeeId={employeeId} /></Box>} />
        <FixedSizeDialog open={openEditD} handleClose={() => setOpenEditD(false)} dialogHeading="Edit Deduction" negativeResponse="Cancel" maxWidth="md"
          content={<Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}><EmployeeDeductionForm formType="edit" setOpen={setOpenEditD} setNewEmployeeDeductionData={setNewEmployeeDeductionData} setIsSuccess={setIsSuccess} editData={editD} employeeId={employeeId} /></Box>} />
        <FixedSizeDialog open={openDelD} handleClose={() => setOpenDelD(false)} dialogHeading="Delete deduction?" negativeResponse="Cancel"
          positiveResponse="Yes, delete" handleSubmit={async () => { await requester.post(`/human_resource/employees/payroll/delete/${clickedD}`); setIsSuccess(true); setNewEmployeeDeductionData(true); setOpenDelD(false); }} maxWidth="sm" content={deleteWarning(isDark)} />
      </PayrollSection>
    </Box>
  );
}